/**
 * Cliente del worker de reslice oblicuo. Fallback síncrono en hilo principal.
 */
import { getMprObliqueResliceWorkerSource } from './mprObliqueResliceWorker'
import {
  packVolumeSlices,
  resliceObliquePlanePacked,
} from './mprObliqueReslice'

const INIT_TIMEOUT_MS = 20000
const RESLICE_TIMEOUT_MS = 30000

let shared = null

function canUseWorker() {
  return typeof Worker !== 'undefined' && typeof Blob !== 'undefined'
}

export function getObliqueResliceClient() {
  if (!shared) shared = new ObliqueResliceClient()
  return shared
}

export function disposeObliqueResliceClient() {
  if (shared) {
    shared.dispose()
    shared = null
  }
}

export class ObliqueResliceClient {
  constructor() {
    this._worker = null
    this._url = null
    this._ready = false
    this._volKey = ''
    this._packed = null
    this._meta = null
    this._seq = 0
    this._pending = new Map()
  }

  dispose() {
    this._pending.forEach((p) => {
      try {
        p.reject(new Error('disposed'))
      } catch (e) {
        /* ignore */
      }
    })
    this._pending.clear()
    this._terminateWorker()
    this._packed = null
    this._meta = null
    this._volKey = ''
    this._ready = false
  }

  _terminateWorker() {
    if (this._worker) {
      try {
        this._worker.terminate()
      } catch (e) {
        /* ignore */
      }
    }
    this._worker = null
    if (this._url) {
      try {
        URL.revokeObjectURL(this._url)
      } catch (e) {
        /* ignore */
      }
    }
    this._url = null
    this._ready = false
  }

  _ensureWorker() {
    if (this._worker) return true
    if (!canUseWorker()) return false
    try {
      const src = getMprObliqueResliceWorkerSource()
      const blob = new Blob([src], { type: 'application/javascript' })
      this._url = URL.createObjectURL(blob)
      this._worker = new Worker(this._url)
      this._worker.onmessage = (ev) => this._onMessage(ev)
      this._worker.onerror = () => {
        this._terminateWorker()
      }
      return true
    } catch (e) {
      this._terminateWorker()
      return false
    }
  }

  _onMessage(ev) {
    const msg = (ev && ev.data) || {}
    const pending = this._pending.get(msg.id)
    if (!pending) return
    this._pending.delete(msg.id)
    if (pending.timer) clearTimeout(pending.timer)
    if (msg.type === 'error') {
      pending.reject(new Error(msg.error || 'worker-error'))
      return
    }
    pending.resolve(msg)
  }

  _post(payload, transfer, timeoutMs) {
    return new Promise((resolve, reject) => {
      if (!this._worker) {
        reject(new Error('no-worker'))
        return
      }
      const id = `obliq-${++this._seq}`
      const timer = setTimeout(() => {
        this._pending.delete(id)
        reject(new Error('timeout'))
      }, timeoutMs)
      this._pending.set(id, { resolve, reject, timer })
      try {
        if (transfer && transfer.length) {
          this._worker.postMessage({ ...payload, id }, transfer)
        } else {
          this._worker.postMessage({ ...payload, id })
        }
      } catch (e) {
        this._pending.delete(id)
        clearTimeout(timer)
        reject(e)
      }
    })
  }

  isReady(key) {
    if (!this._ready || !this._packed || !this._worker) return false
    if (key != null && String(key) !== this._volKey) return false
    return true
  }

  /**
   * Solo empaqueta en main (sin esperar al worker).
   * @param {{
   *   key: string,
   *   cols: number,
   *   rows: number,
   *   zDim: number,
   *   getSlice: (z:number)=>Int16Array|null,
   * }} args
   */
  ensurePackedSync(args) {
    const key = String(args.key || '')
    const cols = Math.max(1, args.cols | 0)
    const rows = Math.max(1, args.rows | 0)
    const zDim = Math.max(1, args.zDim | 0)
    if (key && key === this._volKey && this._packed) {
      return this._packed
    }
    this._packed = packVolumeSlices(args.getSlice, cols, rows, zDim)
    this._meta = { cols, rows, zDim }
    this._volKey = key
    this._ready = false
    return this._packed
  }

  /**
   * Empaqueta (si hace falta) e inicializa el worker.
   * @param {{
   *   key: string,
   *   cols: number,
   *   rows: number,
   *   zDim: number,
   *   getSlice: (z:number)=>Int16Array|null,
   * }} args
   */
  async ensureVolume(args) {
    const key = String(args.key || '')
    if (this.isReady(key)) return true
    this.ensurePackedSync(args)
    if (!this._ensureWorker()) return false
    const copy = this._packed.slice()
    try {
      await this._post(
        {
          type: 'init',
          cols: this._meta.cols,
          rows: this._meta.rows,
          zDim: this._meta.zDim,
          volume: copy.buffer,
        },
        [copy.buffer],
        INIT_TIMEOUT_MS
      )
      this._ready = true
      return true
    } catch (e) {
      this._ready = false
      return false
    }
  }

  /**
   * @param {object[]} jobs
   * @returns {Promise<Int16Array[]>}
   */
  async resliceJobs(jobs) {
    if (this._ready && this._worker && this._meta) {
      try {
        const msg = await this._post(
          { type: 'reslice', jobs, meta: null },
          null,
          RESLICE_TIMEOUT_MS
        )
        if (msg && msg.planes) {
          return msg.planes.map((buf) => new Int16Array(buf))
        }
      } catch (e) {
        /* fallback sync */
      }
    }
    return this.resliceJobsSync(jobs)
  }

  /**
   * Fallback síncrono con buffer empaquetado en main.
   * @param {object[]} jobs
   */
  resliceJobsSync(jobs) {
    if (!this._packed || !this._meta) {
      throw new Error('no-packed-volume')
    }
    const { cols, rows, zDim } = this._meta
    return (jobs || []).map((job) =>
      resliceObliquePlanePacked({
        ...job,
        volume: this._packed,
        cols,
        rows,
        zDim,
      })
    )
  }
}
